import React from "react";
import type { ComponentProps, ReactElement } from "react";

import { format } from "date-fns";
import { ChevronDownIcon, ChevronUpIcon, Text } from "native-base";
import type { Icon, IIconProps, ITextProps } from "native-base";

import {
  DrumsIcon,
  GuitarIcon,
  OneOnOneIcon,
  PairedIcon,
  PianoIcon,
  ViolinIcon,
  TimetableIcon,
  CloseIcon,
  PendingIcon,
  BlockIcon,
  DoubleTickIcon,
  TickIcon,
  NoLessonIcon,
  LeftSchoolIcon,
  MusicTheoryIcon,
  RecorderIcon,
  UkeleleIcon,
  SwapIcon,
  GroupIcon,
} from "pianofunclub-shared/components/Other/Icons";
import type { LessonBlockDataNode } from "pianofunclub-shared/components/Registers/TeacherRegisters";
import type { AlertError } from "pianofunclub-shared/types";
import {
  getLessonDayString,
  getLessonTimeAsDate,
  titleCaseConverter,
} from "pianofunclub-shared/utils/converters";

export const extractConnectionText = (
  connection: any, // eslint-disable-line @typescript-eslint/no-explicit-any
  objectName: string
): string | undefined => {
  if (Array.isArray(connection?.edges)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return connection.edges.map((x: any) => x.node[objectName]).join(", ");
  }
};

export const mapErrorToAlertText = (errors?: unknown): AlertError => {
  let errorCode;
  let alertHeader = "";
  let alertMessage = "Have another go!";
  // graphql_auth error responses are objects
  if (typeof errors === "object") {
    // @ts-expect-error errorType is unknown
    errorCode = Object.values(errors)?.[0]?.[0]?.code;
    if (!errorCode) {
      // @ts-expect-error errorType is unknown
      errorCode = errors?.nonFieldErrors?.[0]?.code;
    }
    // custom error responses are arrays
  } else if (Array.isArray(errors)) {
    errorCode = errors?.[0]?.code;
  }
  switch (errorCode) {
    case "unique":
      alertHeader = "A user with that email already exists";
      alertMessage = "Have another go with a different email address.";
      break;
    case "invalid_credentials":
      alertHeader = "Those details weren't right...";
      break;
    case "invalid_password":
      alertHeader = "The password you entered is incorrect";
      alertMessage = "It must be at least 8 characters long, and contian both letters & numbers.";
      break;
    case "password_mismatch":
      alertHeader = "Those passwords didn't match";
      break;
    case "password_too_short":
      alertHeader = "That password is too short";
      alertMessage = "It must be at least 8 characters long.";
      break;
    case "password_too_similar":
      alertHeader = "Password is too similar to your email";
      break;
    case "password_too_common":
      alertHeader = "Password is too common";
      break;
    case "password_entirely_numeric":
      alertHeader = "Password must contain both letters and numbers";
      break;
    case "email_fail":
      alertHeader = "Sorry, something went wrong on our end...";
      alertMessage =
        "Have another go, and contact support@pianofunclub.co.uk if it's still not working.";
      break;
    case "invalid_token":
      alertHeader = "The link you used is no longer valid";
      alertMessage = "Please request a new email and we'll send you an updated link.";
      break;
    default:
      alertHeader = "That didn't work...";
  }
  return {
    alertHeader: alertHeader,
    alertMessage: alertMessage,
    code: errorCode,
  };
};

export const getTrueKeys = (obj: Record<string, boolean>): string => {
  return Object.keys(obj)
    .filter((key) => obj[key])
    .join();
};

export const getStartingYear = (): number => {
  const currentDate = new Date();
  if (currentDate.getMonth() < 7) {
    return currentDate.getFullYear() - 1;
  } else {
    return currentDate.getFullYear();
  }
};

export const getStartingYearFromBlock = (
  block?: number | null,
  year?: number | null
): number | null => {
  if (block && year) {
    if (block > 2) {
      return year - 1;
    } else {
      return year;
    }
  } else {
    return null;
  }
};

export const getDefaultStartingYearAndStartingYearsForSelect = (): [
  number,
  { label: string; value: number }[],
] => {
  const currentDate = new Date();
  const largestStartingYear =
    currentDate.getMonth() < 5 ? currentDate.getFullYear() - 1 : currentDate.getFullYear();
  const startingYearArray = [...Array(largestStartingYear - 2021).keys()].map((i) => i + 2022);
  const defaultStartingYear =
    currentDate.getMonth() >= 5 && currentDate.getMonth() < 7
      ? largestStartingYear - 1
      : largestStartingYear;

  return [
    defaultStartingYear,
    startingYearArray
      .map((item) => {
        return { label: `${item}/${item + 1}`, value: item };
      })
      .reverse(),
  ];
};

export const getYearsForSelect = () => {
  const yearArray = [...Array(new Date().getFullYear() - 2022).keys()].map((i) => i + 2023);
  return yearArray.map((item) => {
    return { label: String(item), value: String(item) };
  });
};

export const getCurrentBlock = (): number => {
  const currentMonth = new Date().getMonth();
  if (currentMonth < 2) {
    return 3;
  } else if (currentMonth < 3) {
    return 4;
  } else if (currentMonth < 5) {
    return 5;
  } else if (currentMonth < 7) {
    return 6;
  } else if (currentMonth < 10) {
    return 1;
  } else {
    return 2;
  }
};

export const getCurrentTerm = (): number => {
  const currentMonth = new Date().getMonth();
  if (currentMonth < 3) {
    return 2;
  } else if (currentMonth < 7) {
    return 3;
  } else {
    return 1;
  }
};

export const getYears = (since: number): { label: string; value: number }[] => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const yearArray = [...Array(currentYear - since).keys()].map((i) => i + (since + 1));
  return yearArray.map((item) => {
    return { label: item.toString(), value: item };
  });
};

export const getReduxHomeworkId = (term: string, lesson: string, pupilId: string): string => {
  return `homework_${term}_${lesson}_${pupilId}`;
};

export const reverseOrderBy = (
  orderBy: string,
  options?: {
    onlyReverseOrderOfPrimarySort?: boolean;
  }
) => {
  const fields = orderBy.split(",").map((field) => field);
  const reversedFields = fields.map((field, index) => {
    if (options?.onlyReverseOrderOfPrimarySort && index > 0) return field;

    if (field.startsWith("-")) {
      return field.substring(1); // remove '-' to make ascending
    } else {
      return `-${field}`; // prepend '-' to make descending
    }
  });
  return reversedFields.join(",");
};

export const sortIconExtractor = (columnName: string, orderBy: string | undefined) => {
  if (!orderBy) {
    return <ChevronDownIcon color="surface.400" />;
  }

  const reversedOrderBy = reverseOrderBy(orderBy);

  if (orderBy === columnName) {
    return <ChevronDownIcon color="surface.900" />;
  }

  if (reversedOrderBy === columnName) {
    return <ChevronUpIcon color="surface.900" />;
  }

  return <ChevronDownIcon color="surface.400" />;
};

export const getFullName = (firstName?: string | null, lastName?: string | null) => {
  return `${firstName ? titleCaseConverter(firstName) : ""}${lastName ? " " + titleCaseConverter(lastName) : ""}`;
};

export const getPreviousBlockAndStartingYear = (
  block?: number | null,
  startingYear?: number | null
) => {
  if (!block || !startingYear) {
    return [null, null];
  }

  if (block == 1) {
    return [6, startingYear - 1];
  } else {
    return [block - 1, startingYear];
  }
};

export const getTermInWords = (block?: number | null, startingYear?: number | null): string => {
  if (!block || !startingYear) {
    return "";
  }
  const half = block % 2 === 1 ? "1st" : "2nd";
  let termName;
  if (block <= 2) {
    termName = "Autumn";
  } else if (block <= 4) {
    termName = "Spring";
  } else {
    termName = "Summer";
  }
  return `${half} Half ${termName} ${block <= 2 ? startingYear : Number(startingYear) + 1}`;
};

export const getLessonTypeInWords = (
  lessonType: string | undefined | null,
  lessonDuration?: number | null
): string => {
  if (typeof lessonType !== "string" || typeof lessonDuration !== "number") {
    return "";
  }
  return `${titleCaseConverter(lessonType)} ${lessonDuration} Minutes`;
};

export const getInstrumentIcon = (
  instrumentName?: string | null,
  iconProps?: ComponentProps<typeof Icon>
): ReactElement | null => {
  switch (instrumentName) {
    case "PIANO":
      return <PianoIcon {...iconProps} />;
    case "GUITAR":
      return <GuitarIcon {...iconProps} />;
    case "VIOLIN":
      return <ViolinIcon {...iconProps} />;
    case "DRUMS":
      return <DrumsIcon {...iconProps} />;
    case "RECORDER":
      return <RecorderIcon {...iconProps} />;
    case "UKELELE":
      return <UkeleleIcon {...iconProps} />;
    case "THEORY":
      return <MusicTheoryIcon {...iconProps} />;
    default:
      return null;
  }
};

export const getLessonTypeIcon = (
  lessonType: string | undefined | null,
  iconProps?: ComponentProps<typeof Icon>
): ReactElement | null => {
  switch (lessonType) {
    case "INDIVIDUAL":
      return <OneOnOneIcon size={5} {...iconProps} />;
    case "PAIRED":
      return <PairedIcon mr="1" size={6} {...iconProps} />;
    default:
      return <GroupIcon mr="1" size={6} {...iconProps} />;
  }
};

export const getLessonStatuses = ({
  blankText,
  iconProps,
  showBlankIcon,
  useShortLabels,
}: {
  blankText?: string;
  iconProps?: IIconProps;
  showBlankIcon?: boolean;
  useShortLabels?: boolean;
}): {
  color: string;
  icon?: ReactElement;
  label: string;
  textColor: string;
  value: string;
}[] => {
  return [
    {
      color: "primary.100",
      icon: <TickIcon color="primary.600" size="7" {...iconProps} />,
      label: "Present",
      textColor: "primary.600",
      value: "PRESENT",
    },
    {
      color: "primary.100",
      icon: <DoubleTickIcon color="primary.600" size="7" {...iconProps} />,
      label: useShortLabels ? "Present (x2)" : "Present (Double)",
      textColor: "primary.600",
      value: "PRESENT_DOUBLE",
    },
    {
      color: "red.100",
      icon: <CloseIcon color="red.500" size="6" {...iconProps} />,
      label: "Pupil Absent",
      textColor: "red.500",
      value: "PUPIL_ABSENT",
    },
    {
      color: "red.100",
      icon: <CloseIcon color="red.500" size="6" {...iconProps} />,
      label: "Teacher Absent",
      textColor: "red.500",
      value: "TEACHER_ABSENT",
    },
    {
      color: "muted.200",
      icon: <BlockIcon color="muted.600" size="6" {...iconProps} />,
      label: "Stopped",
      textColor: "muted.500",
      value: "STOPPED_LESSONS",
    },
    {
      color: "muted.200",
      icon: <LeftSchoolIcon color="muted.600" size="6" {...iconProps} />,
      label: "Left School",
      textColor: "muted.500",
      value: "LEFT_SCHOOL",
    },
    {
      color: "red.100",
      icon: <CloseIcon color="red.500" size="6" {...iconProps} />,
      label: "Lesson Missed",
      textColor: "red.500",
      value: "MISSED",
    },
    {
      color: "muted.200",
      icon: <NoLessonIcon color="muted.600" size="6" {...iconProps} />,
      label: "No Lesson",
      textColor: "muted.500",
      value: "NO_LESSON",
    },
    {
      color: "muted.200",
      icon: <SwapIcon color="muted.600" size="6" {...iconProps} />,
      label: "Other Teacher",
      textColor: "muted.500",
      value: "OTHER_TEACHER",
    },
    {
      color: "yellow.100",
      icon: <TimetableIcon color="yellow.500" size="6" {...iconProps} />,
      label: "Break",
      textColor: "muted.500",
      value: "BREAK",
    },
    {
      color: "transparent",
      icon: showBlankIcon ? <PendingIcon color="muted.400" size="6" {...iconProps} /> : undefined,
      label: blankText ?? "Mark Register",
      textColor: "muted.400",
      value: "BLANK",
    },
  ];
};

export const getAccountTypeEmoji = (accountType: string, props?: ITextProps): ReactElement => {
  switch (accountType) {
    case "PARENT":
      return (
        <Text fontSize="8xl" textAlign="center" {...props}>
          {"🧓"}
        </Text>
      );
    case "PUPIL":
      return (
        <Text fontSize="8xl" textAlign="center" {...props}>
          {"🧒"}
        </Text>
      );
    case "TEACHER":
      return (
        <Text fontSize="8xl" textAlign="center" {...props}>
          {"👨"}
        </Text>
      );
    default:
      return (
        <Text fontSize="8xl" textAlign="center" {...props}>
          {"🧐"}
        </Text>
      );
  }
};

export const getErroredInvoiceLabel = (
  accountNumber: string | null | undefined,
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  email: string | null | undefined
): string => {
  let label = accountNumber ? accountNumber + " • " : "";
  if (firstName) {
    label += getFullName(firstName, lastName);
  } else {
    label += email ?? "";
  }
  return label;
};

export const getErroredBulkCommunicationLabel = (
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  email: string | null | undefined
): string => {
  let label = "";
  if (firstName) {
    label += getFullName(firstName, lastName);
    if (email) {
      label += " • ";
    }
  }

  label += email ?? "";

  return label;
};

export const getOrderBy = (
  orderBy: string,
  previousOrderBy: string | undefined,
  options?: {
    onlyReverseOrderOfPrimarySort?: boolean;
  }
) => {
  if (!previousOrderBy) {
    return orderBy;
  }

  const reversedOrderBy = reverseOrderBy(orderBy, options);

  if (previousOrderBy !== orderBy && previousOrderBy !== reversedOrderBy) {
    return orderBy;
  }

  if (orderBy === previousOrderBy) {
    return reversedOrderBy;
  }

  return orderBy;
};

export const getSlotOfferMessageSubject = (variables: { pupilFirstName: string }) => {
  const { pupilFirstName } = variables;

  return `Music Lesson Available for ${pupilFirstName}`;
};

export const getSlotOfferMessageString = (variables: {
  lessonBlockData: LessonBlockDataNode;
  parentName: string;
  pupilFirstName: string;
}) => {
  const { lessonBlockData, parentName, pupilFirstName } = variables;

  const costPerLesson = lessonBlockData.lessonStage?.costPerLesson;
  const lessonType = lessonBlockData.lessonStage?.lessonType;
  const lessonDuration = lessonBlockData.lessonStage?.lessonDuration;
  const lessonDescription = `${lessonType === "INDIVIDUAL" ? "an" : "a"} ${lessonType?.toLowerCase()} ${lessonDuration} minute`;
  const lessonDayString = getLessonDayString(lessonBlockData.lessonDay);
  const lessonTime = getLessonTimeAsDate(lessonBlockData.lessonTime);
  const instrumentName = lessonBlockData?.instrument?.name;

  return `Dear ${parentName},

We're happy to let you know that we now have a slot for music lessons at school for ${pupilFirstName} - the slot is for ${lessonDescription} ${instrumentName} lesson on ${lessonDayString}s${lessonTime ? " at " + format(lessonTime, "H:mm a") : ""}.

These cost £${costPerLesson} per lesson and are booked in blocks of 5 lessons each half-term. You will only be charged for lessons delivered.

{{EMAIL: We will hold this slot for the next 24 hours. Please reply to this email to let us know if you would like us to enrol ${pupilFirstName} for music lessons, and confirm your child's year group and class.}}
{{SMS: We will hold this slot for the next 24 hours. Please send us an email at admin@pianofunclub.co.uk to let us know if you would like us to enrol ${pupilFirstName}. Please do not reply directly to this text as this number is not monitored.}}

All the best,

The PianoFunClub Team`;
};
